import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import { selectSelectedCity, startStrikeFromCity } from "src/store/ui"
import {
  selectCurrentPlayer,
  selectCurrentTeam,
  selectGame,
  selectResearchLevels,
  selectResearchPoints,
  selectViewerId,
} from "src/store/game"

import constants from "src/constants"
import wsClient from "src/wsClient"
import ModalService from "src/modals"
import { buildings } from "src/assets/images"
import Modal from "src/components/modals/Modal"
import { UPGRADE_CURRENCY_ICON, upgradeGate } from "src/utils/buildingCosts"
import { upgradeOrdersFor } from "src/utils/cellActions"
import { cellFoodProduction } from "src/utils/foodOutlook"
import {
  capacityMultiplier,
  missileDamageMultiplier,
  populationGrowthMultiplier,
  productionMultiplier,
  researchOutput,
  siloCooldown,
  siloRange,
  tunnelLoadCapacity,
  tunnelNetworkCapacity,
  wallHealthMultiplier,
} from "src/utils/gameModifiers"
import UnderConstruction from "src/components/UnderConstruction"

import "./BuildingModal.css"
import { populationCeiling, scale } from "src/utils/ladder"

// Display names live in the `city.buildings.*` translation keys (camelCased backend code).
const buildingNameKey = (code) => `city.buildings.${code.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`

// What the wall absorbs before it is breached (Q7a: its own constant, sized by the owner's
// calibration, not the generic building health). `_wall_capacity` multiplies by Impenetrable,
// so quoting the base here understated the wall of exactly the player who had researched it.
const wallHealth = (gameConfig, level, multiplier = 1) =>
  Math.trunc((gameConfig?.rules_config?.WALL_HEALTH_PER_LEVEL ?? 0) * scale(level) * multiplier)

const researchContent = (t, gameConfig, level, name, currency) => {
  // Doubling per level (owner, 2026-09-29) — the server's `research_output`, never a local copy.
  const pointsAt = (buildingLevel) => researchOutput({ rules: gameConfig?.rules_config, level: buildingLevel })
  return (
    <>
      <div className="building-description">
        <p className="p-small">{t("city.building.researchIntro", { name, currency })}</p>
        {/* Research is *executed* on the global screen behind the points counter — the owner's
            decision of 2026-08-20. This modal is about the building: it generates the pool. The
            door to that screen is in the action row at the foot of the sheet, beside the upgrade
            (owner, 2026-09-10) — it is a thing to press, and the presses live together. */}
        <p className="p-small">{t("research.executedGlobally")}</p>
      </div>
      <div className="building-level-section">
        <h3>{t("city.building.outputPerRound")}</h3>
        <div className="building-level-stats">
          <div className="building-level-stats-box">
            <div>
              <h3>{t("city.building.levelHeading", { level })}</h3>
              <p className="p-mini">{t("city.building.points", { amount: pointsAt(level) })}</p>
            </div>
          </div>
          <div className="building-level-stats-box">
            <div>
              <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
              <p className="p-mini">{t("city.building.points", { amount: pointsAt(level + 1) })}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* `mods` carries the research and race multipliers the server applies to these very numbers. The
   per-level constants are scaled once, where each is read, so the arithmetic below stays readable
   — and so a screen that says "120 food / round" says what the player's next tick will actually
   bring rather than what an un-researched, raceless player would get. */
const contentPerBuilding = (t, buildingCode, level, gameConfig, mods = {}) => {
  const {
    wall: wallMultiplier = 1,
    production: productionMod = 1,
    capacity: capacityMod = 1,
    populationGrowth: growthMod = 1,
    missileDamage: missileMod = 1,
    // For the cultivation card's *real* current output — a short-staffed farm yields less than
    // its rated PRODUCTION_PER_LEVEL, and the card used to quote the rating as though it were the
    // harvest.
    team,
    race,
    researchLevels,
    workers = 0,
  } = mods
  if (buildingCode === constants.buildings.human.WALL) {
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.wallIntro")}</p>
          <p className="p-small">{t("city.building.wallDetail")}</p>
        </div>
        <div className="building-level-section">
          <h3>{t("city.building.combatStats")}</h3>
          <div className="building-level-stats">
            {/* Both real figures from the config: what it soaks before the breach, and — since
                the owner's Q7/Q7a — what it fires each exchange while it stands. */}
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.absorbs", { amount: wallHealth(gameConfig, level, wallMultiplier) })}</p>
                <p className="p-mini">
                  {t("city.building.wallAttack", {
                    amount: (gameConfig?.rules_config?.WALL_ATTACK_PER_LEVEL ?? 0) * scale(level),
                  })}
                </p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">{t("city.building.absorbs", { amount: wallHealth(gameConfig, level + 1, wallMultiplier) })}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.human.COMMAND_CENTER) {
    const centre = gameConfig.human_config.BUILDINGS.command_center
    // Prosperity and the Economist raise the rate workers arrive; nothing scales the ceiling they
    // fill to, which is the price's alone (`build_from_command_center`).
    const workersPerLevel = Math.trunc(centre.WORKERS_PER_LEVEL * growthMod)
    // The ceiling is twice what the city's next level costs (requirement 34's D4), so it is a
    // function of the level rather than a figure per level.
    const workerCeiling = (at) =>
      Math.trunc(populationCeiling(at, centre.UPGRADE_A, centre.UPGRADE_B, gameConfig?.ladder?.cap_headroom))
    // Three distinct numbers, and the modal names all three (owner's ask, 2026-09-02): the workers
    // grown a round, the worker ceiling, and how many workers a round the queue may spend — two
    // different values even where they read alike today, so the modal keeps them apart rather than
    // hiding one. That last is a *worker* budget, the mirror of the hatchery's meat one:
    // `build_from_command_center` measures the queue's cost in workers against it, so wording it
    // as units said a level-1 centre builds 100 tanks a round when it builds four. No multiplier
    // on it either: the command centre's build budget is flat, unlike the hatchery's.
    const productionCapacityPerLevel = centre.PRODUCTION_CAPACITY_PER_LEVEL
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.commandCenterIntro")}</p>
          <p className="p-small">{t("city.building.commandCenterDetail")}</p>
        </div>

        <div className="building-level-section">
          <div className="building-level-stats">
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.workersPerRound", { amount: workersPerLevel * scale(level) })}</p>
                <p className="p-mini">{t("city.building.maxWorkers", { amount: workerCeiling(level) })}</p>
                <p className="p-mini">{t("city.building.maxProduction", { amount: productionCapacityPerLevel * scale(level) })}</p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">{t("city.building.workersPerRound", { amount: workersPerLevel * scale(level + 1) })}</p>
                <p className="p-mini">{t("city.building.maxWorkers", { amount: workerCeiling(level + 1) })}</p>
                <p className="p-mini">
                  {t("city.building.maxProduction", { amount: productionCapacityPerLevel * scale(level + 1) })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.human.CULTIVATION_CENTER) {
    const requiredWorkers = gameConfig.human_config.BUILDINGS.cultivation_center.REQUIRED_WORKERS_PER_LEVEL
    // Current level: what this farm actually harvests with the workers standing in it right now.
    // Next level: the rating at full staff — the upside of upgrading, with its worker cost beside.
    const currentReal = cellFoodProduction({ gameConfig, team, race, researchLevels, level, workers })
    const nextFullStaff = cellFoodProduction({
      gameConfig,
      team,
      race,
      researchLevels,
      level: level + 1,
      workers: requiredWorkers * scale(level + 1),
    })
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.cultivationIntro")}</p>
          <p className="p-small">{t("city.building.cultivationDetail")}</p>
          <p className="p-small">{t("city.building.cultivationWorkers")}</p>
        </div>

        <div className="building-level-section">
          <h3>{t("city.building.foodGenerationRate")}</h3>
          <div className="building-level-stats">
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.foodPerRound", { amount: currentReal })}</p>
                <p className="p-mini">{t("city.building.workers", { amount: requiredWorkers * scale(level) })}</p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">{t("city.building.foodPerRound", { amount: nextFullStaff })}</p>
                <p className="p-mini">{t("city.building.workers", { amount: requiredWorkers * scale(level + 1) })}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.zombie.HATCHERY) {
    const productionCapacityPerLevel = Math.trunc(
      gameConfig.zombie_config.BUILDINGS.hatchery.PRODUCTION_CAPACITY_PER_LEVEL * capacityMod
    )
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.hatcheryIntro")}</p>
          <p className="p-small">{t("city.building.hatcheryDetail")}</p>
        </div>

        <div className="building-level-section">
          <h3>{t("city.building.meatConsumptionCapacity")}</h3>
          <div className="building-level-stats">
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.meatPerRound", { amount: productionCapacityPerLevel * scale(level) })}</p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">{t("city.building.meatPerRound", { amount: productionCapacityPerLevel * scale(level + 1) })}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.zombie.PUTREFACTOR) {
    const production = Math.trunc(
      gameConfig.zombie_config.BUILDINGS.putrefactor.PRODUCTION_PER_LEVEL * productionMod
    )
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.putrefactorIntro")}</p>
          <p className="p-small">{t("city.building.putrefactorDetail")}</p>
        </div>

        <div className="building-level-section">
          <h3>{t("city.building.meatGenerationRate")}</h3>
          <div className="building-level-stats">
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.meatPerRound", { amount: production * scale(level) })}</p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">{t("city.building.meatPerRound", { amount: production * scale(level + 1) })}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.human.ACADEMY) {
    return researchContent(t, gameConfig, level, t("city.buildings.academy"), t("city.building.researchPoints"))
  } else if (buildingCode === constants.buildings.zombie.EVOLUTION_CHAMBER) {
    return researchContent(
      t,
      gameConfig,
      level,
      t("city.buildings.evolutionChamber"),
      t("city.building.researchPoints")
    )
  } else if (buildingCode === constants.buildings.human.MISSILE_SILO) {
    const silo = gameConfig.human_config.BUILDINGS.missile_silo
    // Reach and reload are the research's, not the level's (S13): the level buys damage only, so
    // both boxes quote the same reach and the same reload — the program's later levels applied to the
    // reach, and the reload fixed at the configured three (U6).
    const reload = siloCooldown({ gameConfig, researchLevels })
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.siloIntro")}</p>
          <p className="p-small">{t("city.building.siloDetail")}</p>
          <p className="p-small">{t("city.building.siloResearchNote")}</p>
        </div>
        <div className="building-level-section">
          <h3>{t("city.building.strike")}</h3>
          <div className="building-level-stats">
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level })}</h3>
                <p className="p-mini">{t("city.building.range", { amount: siloRange({ gameConfig, researchLevels, level }) })}</p>
                <p className="p-mini">
                  {t("city.building.damage", { amount: Math.trunc(silo.STRIKE_DAMAGE_PER_LEVEL * scale(level) * missileMod) })}
                </p>
                <p className="p-mini">{t("city.building.reload", { amount: reload })}</p>
              </div>
            </div>
            <div className="building-level-stats-box">
              <div>
                <h3>{t("city.building.levelHeading", { level: level + 1 })}</h3>
                <p className="p-mini">
                  {t("city.building.range", { amount: siloRange({ gameConfig, researchLevels, level: level + 1 }) })}
                </p>
                <p className="p-mini">
                  {t("city.building.damage", {
                    amount: Math.trunc(silo.STRIKE_DAMAGE_PER_LEVEL * scale(level + 1) * missileMod),
                  })}
                </p>
                <p className="p-mini">{t("city.building.reload", { amount: reload })}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } else if (buildingCode === constants.buildings.zombie.TUNNEL) {
    // The level buys the entry size, doubling (S13, kept by the rework; 2026-09-30); the tunnel
    // program sizes the network, so the sheet quotes both and nothing about reach or reopening.
    const holds = tunnelNetworkCapacity({ gameConfig, researchLevels })
    const box = (atLevel) => (
      <div className="building-level-stats-box">
        <div>
          <h3>{t("city.building.levelHeading", { level: atLevel })}</h3>
          <p className="p-mini">{t("city.building.tunnelCapacity", { amount: tunnelLoadCapacity({ gameConfig, level: atLevel }) })}</p>
          <p className="p-mini">{t("city.building.tunnelNetwork", { amount: holds })}</p>
        </div>
      </div>
    )
    return (
      <>
        <div className="building-description">
          <p className="p-small">{t("city.building.tunnelIntro")}</p>
          <p className="p-small">{t("city.building.tunnelDetail")}</p>
          <p className="p-small">{t("city.building.tunnelResearchNote")}</p>
        </div>
        <div className="building-level-section">
          <div className="building-level-stats">
            {box(level)}
            {box(level + 1)}
          </div>
        </div>
      </>
    )
  } else {
    return <p>{t("city.building.noContent")}</p>
  }
}

const BuildingModal = ({ buildingCode, level }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const gameConfig = useSelector(selectGameConfig)
  const city = useSelector(selectSelectedCity)
  const game = useSelector(selectGame)
  const userId = useSelector(selectViewerId)
  const team = useSelector(selectCurrentTeam)
  const researchLevels = useSelector(selectResearchLevels)
  const race = useSelector(selectCurrentPlayer)?.race
  const points = useSelector(selectResearchPoints)
  const workersHere =
    game?.metadata?.cells?.[`${city?.col}-${city?.row}`]?.metadata_by_user?.[userId]?.units?.[
      constants.units.human.WORKER
    ]?.number || 0
  const content = contentPerBuilding(t, buildingCode, level, gameConfig, {
    wall: wallHealthMultiplier({ gameConfig, team, researchLevels }),
    production: productionMultiplier({ gameConfig, team, race, researchLevels }),
    capacity: capacityMultiplier({ gameConfig, team, researchLevels }),
    populationGrowth: populationGrowthMultiplier({ gameConfig, team, race, researchLevels }),
    missileDamage: missileDamageMultiplier({ gameConfig, race }),
    team,
    race,
    researchLevels,
    workers: workersHere,
  })

  // Research hangs off the Academy / Evolution Chamber, mirroring how troop production hangs off
  // the Command Center / Hatchery.
  const isSilo = buildingCode === constants.buildings.human.MISSILE_SILO
  // Cooldown is on the wire already; firing while reloading is rejected with SiloOnCooldown. Only
  // the *fire* button reads it (S23): a reloading silo upgrades like any other building, and the
  // upgrade gate below deliberately knows nothing about the cooldown.
  const cooldownUntil =
    game?.metadata?.cells?.[`${city?.col}-${city?.row}`]?.metadata_by_user?.[userId]?.buildings?.[
      buildingCode
    ]?.cooldown_until_tick ?? 0
  const reloading = cooldownUntil > (game?.tick ?? 0)
  const siloReady = level > 0 && !reloading
  const startTargeting = () => {
    dispatch(startStrikeFromCity(city))
    ModalService.hideAll()
  }

  const isResearchBuilding =
    buildingCode === constants.buildings.human.ACADEMY ||
    buildingCode === constants.buildings.zombie.EVOLUTION_CHAMBER

  // The tunnel's two presses (2026-09-29): load from this city, unload into it. The map bar knows
  // the refusals; the sheet only needs a built tunnel to offer the doors.
  const isTunnel = buildingCode === constants.buildings.zombie.TUNNEL
  const openTunnel = (mode) => () => {
    ModalService.show("TunnelModal", { city: { col: city.col, row: city.row }, mode })
  }

  const handleUpgrade = () => {
    if (!city) return
    wsClient.send("game", {
      action: "level_up_building",
      column: city.col,
      row: city.row,
      building: buildingCode,
    })
    ModalService.hide()
  }

  // A level ordered this round can be taken back until the round turns (owner, 2026-10-02):
  // nothing was paid, so the hold on its price is simply released. The sheet stays open, and its
  // upgrade button comes back.
  const handleCancelUpgrade = () => {
    if (!city) return
    wsClient.send("game", { action: "cancel_upgrade", column: city.col, row: city.row, building: buildingCode })
  }

  // The same gates the server checks, answered before the click (P2/P3): the button greys out
  // with its reason instead of letting a doomed request travel, and the price names its currency.
  const cellMetadata = game?.metadata?.cells?.[`${city?.col}-${city?.row}`]?.metadata_by_user?.[userId]
  // A level is an order that lands when the round turns (owner, 2026-10-02): one a building a
  // round, priced against what the orders already given hold from the same purse.
  const orders = upgradeOrdersFor({ game, gameConfig, userId, team, race, col: city?.col, row: city?.row, buildingCode })
  const gate = upgradeGate({
    gameConfig,
    team,
    buildingCode,
    level,
    race,
    workersOnCell: cellMetadata?.units?.[constants.units.human.WORKER]?.number ?? 0,
    food: game?.metadata?.users?.[String(userId)]?.food ?? 0,
    ...orders,
  })
  const upgradeCost = gate.cost

  /* The sheet reads top to bottom (owner, 2026-09-10): what it is, then the drawing at the full
     width of the modal and its own proportions — never cropped — then what it does, and the
     priced button last. The plate was a 58px icon beside the name and the drawing might as well
     not have been there. */
  const header = (
    <header className="building-head">
      <div className="building-head-title">
        <h1>{t(buildingNameKey(buildingCode), { defaultValue: buildingCode })}</h1>
        <p className="building-head-meta">
          {isResearchBuilding
            ? `${t("city.building.pointsPerRound", {
                amount: `+${researchOutput({ rules: gameConfig?.rules_config, level })}`,
              })} · ${t("city.building.upgradesImmediately")}`
            : t("city.building.upgradesImmediately")}
        </p>
      </div>

      {isResearchBuilding && (
        <div className="building-head-points">
          <p className="building-points-value">{points}</p>
          <p className="section-label">{t("city.building.pointsLabel")}</p>
        </div>
      )}
    </header>
  )

  const plate = (
    <button
      type="button"
      className={`art-zoomable building-plate-button${orders.ordered ? " upgrading" : ""}`}
      title={t("showcase.open")}
      onClick={() =>
        ModalService.show("ArtShowcaseModal", {
          src: buildings[buildingCode],
          title: t(buildingNameKey(buildingCode), { defaultValue: buildingCode }),
          kicker: `${t("showcase.building")} · ${t(`game.battle.${team}`)}`,
          subtitle: t("city.lvl", { level }),
          team,
        })
      }
    >
      <img src={buildings[buildingCode]} alt={t("showcase.open")} className="building-plate" />
      {/* Pressing the band takes the order back (owner, 2026-10-03), and opens nothing. */}
      {orders.ordered && (
        <span
          role="button"
          tabIndex={0}
          className="building-plate-upgrading"
          title={t("city.cancelUpgradeHint", { level: level + 1 })}
          onClick={(event) => {
            event.stopPropagation()
            handleCancelUpgrade()
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return
            event.stopPropagation()
            handleCancelUpgrade()
          }}
        >
          <UnderConstruction level={level + 1} className="building-plate-construction" decorative />
          <span className="building-plate-upgrading-text">{t("city.upgrading")}</span>
        </span>
      )}
      {/* How far it is built, on the corner of the picture rather than beside the name (owner,
          2026-09-10). The title is the building; the level is a state of it, and the plate is
          where a state badge sits without competing with the name. */}
      <span className="building-plate-level">{t("city.lvl", { level })}</span>
    </button>
  )

  return (
    <Modal width="min(720px, 86%)" isolated={false} scope="map" top="8vh" maxHeight="86vh">
      {header}
      {plate}
      <div className="building-container">
        <div className="building-content-container">{content}</div>
      </div>
      {/* The foot of the sheet is one row of presses (owner, 2026-09-10): the upgrade, and the
          building's own action beside it where it has one — the academy's door to the research
          screen, the silo's fire. With nothing beside it the upgrade fills the row on its own,
          which is every other building and what the sheet looked like before. */}
      <div className="building-actions">
        <button
          className="building-upgrade"
          data-action="upgrade"
          data-building={buildingCode}
          onClick={handleUpgrade}
          disabled={!gate.allowed}
          title={gate.reason ? t(`city.upgradeBlocked.${gate.reason}`) : undefined}
        >
          {orders.ordered ? (
            <>
              <UnderConstruction level={level + 1} decorative /> {t("city.building.upgradePending", { level: level + 1 })}
            </>
          ) : (
            <>
              ⬆ {t("city.building.upgrade")}
              {upgradeCost != null ? ` · ${upgradeCost} ${UPGRADE_CURRENCY_ICON[team] ?? ""}` : ""}
            </>
          )}
        </button>
        {orders.ordered && (
          <button className="building-action building-cancel" data-action="cancel-upgrade" onClick={handleCancelUpgrade}>
            ✕ {t("city.building.cancelUpgrade")}
          </button>
        )}
        {isResearchBuilding && (
          <button className="building-action" onClick={() => ModalService.show("ResearchModal")}>
            🔬 {t("research.openGlobal")}
          </button>
        )}
        {isTunnel && (
          <>
            <button className="building-action" onClick={openTunnel("load")} disabled={level === 0}>
              {level === 0 ? `🕳 ${t("city.notBuilt")}` : `🕳 ${t("city.building.loadTunnel")}`}
            </button>
            <button className="building-action" onClick={openTunnel("unload")} disabled={level === 0}>
              🕳 {t("city.building.unloadTunnel")}
            </button>
          </>
        )}
        {isSilo && (
          <button className="building-action" onClick={startTargeting} disabled={!siloReady}>
            {level === 0
              ? `🚀 ${t("city.notBuilt")}`
              : reloading
              ? `🚀 ${t("city.building.reloadingTick", { tick: cooldownUntil })}`
              : `🚀 ${t("city.building.fire")}`}
          </button>
        )}
      </div>
    </Modal>
  )
}
export default BuildingModal
